import { Options, documentToReactComponents } from '@contentful/rich-text-react-renderer'
import {
  BLOCKS,
  Block,
  Document,
  INLINES,
  Mark,
  NodeData,
  Text,
  TopLevelBlock,
} from '@contentful/rich-text-types'
import { useMemo } from 'react'
import styled from 'styled-components'

import { TypeStyle } from '@syconium/little-miss-figgy/dist/components/TypeStyle'
import { timingDefault } from '@syconium/little-miss-figgy/dist/constants/timing'
import { isUppercase } from '@syconium/little-miss-figgy/dist/lib/utils/isUppercase'

import { NextLink } from '../../app/_components/navigation/NextLink'
import { ContentfulStyledImage } from '../../app/_components/pages/contentful/components/ContentfulStyledImage'
import { ContentfulStyledText } from '../../app/_components/pages/contentful/components/ContentfulStyledText'
import { isDefinedAndNotNull } from '../../app/_utils/isDefinedAndNotNull'

const CenteredImage = styled(ContentfulStyledImage)`
  margin-left: auto;
  margin-right: auto;
`

const LinkContent = styled.span`
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    transition: color ${timingDefault};
    opacity: 0.8;
  }
`

export function ContentfulRichDocumentAsFigsComponents({
  contentfulDocument,
  options,
}: {
  contentfulDocument: unknown
  options?: {
    bodyFontWeight?: string
    bodyFontSize?: keyof typeof TypeStyle
  }
}) {
  const sanitizedDocument = sanitizeContentfulRichDocument(contentfulDocument)

  // prevent the use of css TypeStyle since we want to limit the styling for paragraph text
  const BodyComponent =
    options?.bodyFontSize && options.bodyFontSize !== 'css'
      ? TypeStyle[options.bodyFontSize]
      : TypeStyle['BodyDefault']

  const renderOptions: Options = useMemo(
    () => ({
      renderNode: {
        'styled-text': node => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const props: any = node.data
          return <ContentfulStyledText {...props} />
        },
        'styled-image': node => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const props: any = node.data
          const widths = props.maxWidthDesktop
            ? {
                unit: 'px',
                md: props.maxWidthDesktop,
                sm: props.maxWidthMobile ?? props.maxWidthDesktop,
              }
            : {
                unit: 'vw',
                md: 100,
                sm: 100,
              }
          return <CenteredImage {...props} widths={widths} />
        },
        [BLOCKS.HEADING_1]: (node, children) => {
          const textContent = (node.content[0] as Text | undefined)?.value
          return (
            <TypeStyle.HeadlineMedium
              asTag='h2'
              isUppercase={!!textContent && isUppercase(textContent)}
              style={{ marginBottom: '12px', marginTop: '24px' }}
            >
              {children}
            </TypeStyle.HeadlineMedium>
          )
        },
        [BLOCKS.HEADING_2]: (node, children) => {
          const textContent = (node.content[0] as Text | undefined)?.value
          return (
            <TypeStyle.HeadlineSmall
              asTag='h2'
              isUppercase={!!textContent && isUppercase(textContent)}
              style={{ marginBottom: '12px', marginTop: '20px' }}
            >
              {children}
            </TypeStyle.HeadlineSmall>
          )
        },
        [BLOCKS.HEADING_3]: (node, children) => {
          const textContent = (node.content[0] as Text | undefined)?.value
          return (
            <TypeStyle.HeadlineExtraSmall
              asTag='h3'
              isUppercase={!!textContent && isUppercase(textContent)}
              style={{ marginBottom: '12px', marginTop: '16px' }}
            >
              {children}
            </TypeStyle.HeadlineExtraSmall>
          )
        },
        [BLOCKS.HEADING_4]: (node, children) => {
          const textContent = (node.content[0] as Text | undefined)?.value
          return (
            <TypeStyle.HeadlineExtraExtraSmall
              asTag='h4'
              isUppercase={!!textContent && isUppercase(textContent)}
              style={{ marginBottom: '8px', marginTop: '8px' }}
            >
              {children}
            </TypeStyle.HeadlineExtraExtraSmall>
          )
        },
        [BLOCKS.HEADING_5]: (node, children) => {
          const textContent = (node.content[0] as Text | undefined)?.value
          return (
            <TypeStyle.HeadlineExtraExtraSmall
              asTag='h5'
              isUppercase={!!textContent && isUppercase(textContent)}
              style={{ marginBottom: '8px', marginTop: '8px' }}
            >
              {children}
            </TypeStyle.HeadlineExtraExtraSmall>
          )
        },
        [BLOCKS.HEADING_6]: (node, children) => {
          const textContent = (node.content[0] as Text | undefined)?.value
          return (
            <TypeStyle.HeadlineExtraExtraSmall
              asTag='h6'
              isUppercase={!!textContent && isUppercase(textContent)}
              style={{ marginBottom: '8px', marginTop: '8px' }}
            >
              {children}
            </TypeStyle.HeadlineExtraExtraSmall>
          )
        },
        [BLOCKS.PARAGRAPH]: (_node, children) => {
          return (
            <BodyComponent
              asTag='p'
              style={{ marginBottom: '8px' }}
              fontWeight={options?.bodyFontWeight}
            >
              {children}
            </BodyComponent>
          )
        },
        [INLINES.HYPERLINK]: (node, children) => {
          return (
            <NextLink href={node.data.uri}>
              <BodyComponent asTag='span' fontWeight={options?.bodyFontWeight}>
                <LinkContent>{children}</LinkContent>
              </BodyComponent>
            </NextLink>
          )
        },
      },
    }),
    [BodyComponent, options?.bodyFontWeight]
  )

  return <>{documentToReactComponents(sanitizedDocument, renderOptions)}</>
}

// Type guard for a valid contentful document
export function isValidDocument(doc: unknown): doc is Document {
  return !!doc && typeof doc === 'object' && 'content' in doc
}

export function mergeContentfulRichDocuments(doc1: unknown, doc2: unknown): Document {
  const mergedDocument = sanitizeContentfulRichDocument(doc1)
  mergedDocument.content.push(...sanitizeContentfulRichDocument(doc2).content)
  return mergedDocument
}

export function sanitizeContentfulRichDocument(doc: unknown): Document {
  const defaultDoc: Document = {
    nodeType: BLOCKS.DOCUMENT,
    content: [],
    data: {},
  }

  if (
    typeof doc !== 'object' ||
    !doc ||
    !('content' in doc) ||
    !Array.isArray(doc.content) ||
    !('nodeType' in doc) ||
    doc.nodeType !== BLOCKS.DOCUMENT
  ) {
    return defaultDoc
  }

  return {
    nodeType: BLOCKS.DOCUMENT,
    data: 'data' in doc ? (doc.data as NodeData) : {},
    content: doc.content
      .map(x => sanitizeNode(x) as TopLevelBlock | undefined)
      .filter(isDefinedAndNotNull),
  }
}

type SanitizedNode = TopLevelBlock | Block | Block['content'][number]

function sanitizeNode(node: unknown): SanitizedNode | undefined {
  if (typeof node !== 'object' || !node || !('nodeType' in node)) {
    return undefined
  } else {
    const content =
      'content' in node && Array.isArray(node.content)
        ? node.content.map(x => sanitizeNode(x)).filter(isDefinedAndNotNull)
        : []
    const data = 'data' in node ? (node.data as NodeData) : {}
    const marks = 'marks' in node ? (node.marks as Mark[]) : []
    const value = 'value' in node ? (node.value as string) : undefined

    if (node.nodeType === 'text') {
      return value && value.length > 0
        ? {
            data,
            marks,
            value,
            nodeType: 'text',
          }
        : undefined
    } else {
      // Remove accidental empty paragraphs. People can get spacing in a different way than empty paragraphs and they are often mistakes.
      if (content.length === 0 && node.nodeType === BLOCKS.PARAGRAPH) {
        return undefined
      }
      return {
        data,
        content,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        nodeType: node.nodeType as unknown as any,
      }
    }
  }
}
